import type { JSX } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  PROP_CATEGORIES,
  PROP_CATEGORY_LABELS,
  propsInCategory,
  type EditorTool,
  type PropId,
} from '../../sim/map/index.ts';
import { Tooltip } from '../tooltip.tsx';
import { EditorIcon, TOOL_ICONS } from './icons.tsx';
import { toolDef } from './tools.ts';

const HOLD_MS = 400;
const GAP_PX = 8;
const VIEW_PAD_PX = 8;

type FlyoutBox = { left: number; top: number };

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function placeFlyout(trigger: DOMRect, panel: DOMRect): FlyoutBox {
  const left = trigger.right + GAP_PX;
  const top = trigger.top;
  const maxLeft = window.innerWidth - panel.width - VIEW_PAD_PX;
  const maxTop = window.innerHeight - panel.height - VIEW_PAD_PX;

  return {
    left: clamp(left, VIEW_PAD_PX, Math.max(VIEW_PAD_PX, maxLeft)),
    top: clamp(top, VIEW_PAD_PX, Math.max(VIEW_PAD_PX, maxTop)),
  };
}

export function PlacePropRailButton({
  tool,
  draftProp,
  canPlaceProps,
  onSetTool,
  onSetDraftProp,
}: {
  tool: EditorTool;
  draftProp: PropId;
  canPlaceProps: boolean;
  onSetTool: (tool: EditorTool) => void;
  onSetDraftProp: (prop: PropId) => void;
}): JSX.Element {
  const def = toolDef('place-prop');
  const locked = !canPlaceProps;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const holdTimerRef = useRef<number | null>(null);
  const openedByHoldRef = useRef(false);
  const [open, setOpen] = useState(false);
  const [holding, setHolding] = useState(false);
  const [box, setBox] = useState<FlyoutBox | null>(null);

  const close = (): void => {
    setOpen(false);
    setBox(null);
  };

  const openFlyout = (): void => {
    if (locked) {
      return;
    }

    onSetTool('place-prop');
    setOpen(true);
  };

  const clearHold = (): void => {
    if (holdTimerRef.current !== null) {
      window.clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    setHolding(false);
  };

  useEffect(() => {
    return () => {
      clearHold();
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        close();
      }
    };

    const onPointerDown = (event: PointerEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }

      close();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('pointerdown', onPointerDown, true);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const trigger = buttonRef.current;
    const panel = panelRef.current;

    if (!trigger || !panel) {
      return;
    }

    const layout = (): void => {
      setBox(placeFlyout(trigger.getBoundingClientRect(), panel.getBoundingClientRect()));
    };

    layout();
    window.addEventListener('resize', layout);

    return () => {
      window.removeEventListener('resize', layout);
    };
  }, [open, draftProp]);

  const pick = (id: PropId): void => {
    onSetDraftProp(id);
    onSetTool('place-prop');
    close();
  };

  const root = typeof document === 'undefined' ? null : document.getElementById('ui-root') ?? document.body;

  return (
    <>
      <Tooltip
        label={locked ? 'Place prop — turn Elevation off' : def.label}
        shortcut={locked ? undefined : def.shortcut}
        side="right"
        disabled={open || holding}
      >
        <button
          ref={buttonRef}
          type="button"
          class="editor-rail-button has-flyout"
          data-tool="place-prop"
          aria-label={def.label}
          aria-haspopup="menu"
          aria-expanded={open ? 'true' : 'false'}
          aria-pressed={tool === 'place-prop' ? 'true' : 'false'}
          disabled={locked}
          onPointerDown={(event) => {
            if (event.button !== 0 || locked) {
              return;
            }

            openedByHoldRef.current = false;
            setHolding(true);
            holdTimerRef.current = window.setTimeout(() => {
              holdTimerRef.current = null;
              openedByHoldRef.current = true;
              setHolding(false);
              openFlyout();
            }, HOLD_MS);
          }}
          onPointerUp={clearHold}
          onPointerLeave={clearHold}
          onPointerCancel={clearHold}
          onClick={() => {
            if (openedByHoldRef.current) {
              openedByHoldRef.current = false;
              return;
            }

            if (locked) {
              return;
            }

            if (tool === 'place-prop') {
              openFlyout();
              return;
            }

            onSetTool('place-prop');
          }}
          onContextMenu={(event) => {
            event.preventDefault();
            openFlyout();
          }}
        >
          <EditorIcon icon={TOOL_ICONS['place-prop']} />
          <span class="editor-rail-flyout-caret" aria-hidden="true" />
        </button>
      </Tooltip>
      {open && root
        ? createPortal(
            <div
              ref={panelRef}
              class="editor-prop-flyout ui-panel ui-chrome"
              data-prop-flyout
              role="menu"
              aria-label="Props"
              style={
                box
                  ? { left: `${box.left}px`, top: `${box.top}px` }
                  : { left: 0, top: 0, visibility: 'hidden' }
              }
            >
              {PROP_CATEGORIES.map((category) => (
                <div
                  key={category}
                  class="editor-prop-flyout-group"
                  role="group"
                  aria-label={PROP_CATEGORY_LABELS[category]}
                >
                  <p class="editor-prop-flyout-heading">{PROP_CATEGORY_LABELS[category]}</p>
                  {propsInCategory(category).map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      class="editor-prop-flyout-item"
                      role="menuitem"
                      data-prop={entry.id}
                      aria-pressed={draftProp === entry.id ? 'true' : 'false'}
                      onClick={() => {
                        pick(entry.id);
                      }}
                    >
                      <img
                        src={`/assets/svg/${entry.texture}.svg`}
                        alt=""
                        width={40}
                        height={40}
                      />
                      <span>{entry.label}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>,
            root,
          )
        : null}
    </>
  );
}
